/*
 * The selected segment's (or tab's) fill as one piece that slides to the next choice, as a
 * segmented control's thumb does: where the selected item is inside its strip (the items'
 * offsetParent), measured again when the strip or a label changes size (a font arriving, a
 * count). Null until measured, so the thumb appears in place rather than sliding in from the edge.
 */
import { useLayoutEffect, useState } from 'react'
import type { CSSProperties, RefObject } from 'react'

export function useThumb(strip: RefObject<HTMLElement | null>, selected: RefObject<HTMLElement | null>, key: unknown): CSSProperties | null {
    const [box, setBox] = useState<{ x: number; width: number } | null>(null)
    useLayoutEffect(() => {
        const measure = () => {
            const item = selected.current
            const next = item ? { x: item.offsetLeft, width: item.offsetWidth } : null
            setBox((box) => (box && next && box.x === next.x && box.width === next.width ? box : next))
        }
        measure()
        if (typeof ResizeObserver === 'undefined' || !strip.current) return
        const observer = new ResizeObserver(measure)
        observer.observe(strip.current)
        for (const child of Array.from(strip.current.children)) if (!child.hasAttribute('data-thumb')) observer.observe(child)
        return () => observer.disconnect()
    }, [strip, selected, key])
    return box && { width: box.width, transform: `translateX(${box.x}px)` }
}
